import { useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Card } from "../ui/card";
import { useNetWorthSeries, useProfile } from "../../hooks/queries";
import { useLang } from "../../contexts/LangContext";
import { formatMoney } from "../../lib/api";
import { TrendingUp } from "lucide-react";
import { tx } from "../../i18n";
// iter-170e — shared chart helpers + range pill (was duplicated in
// CashForecastChart). Drops CC of this component from 23 → ~12.
import {
  CHART_AXIS_PROPS,
  CHART_TOOLTIP_STYLE,
  formatCompactNumber,
  formatDateTooltip,
  formatDayLabel,
} from "./chartHelpers";
import { RangeSelector } from "./RangeSelector";

const RANGES = [
  // iter-170e — labels are identical across languages ("30d" is universal).
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
  { value: 180, label: "180d" },
];

export function NetWorthChart() {
  const { lang } = useLang();
  const [days, setDays] = useState(90);
  const { data: profile } = useProfile();
  const profileCurrency = profile?.currency || "USD";
  const [currency, setCurrency] = useState(null); // null = no filter
  const { data, isLoading } = useNetWorthSeries(days, currency);
  const points = data?.points || [];
  const availableCurrencies = data?.available_currencies || [];
  const displayCurrency = currency || profileCurrency;

  const hasEnoughPoints = points.length >= 2;
  const mixedBases = !currency && availableCurrencies.length >= 2;

  return (
    <Card className="p-5 sm:p-6 rounded-2xl border-slate-200" data-testid="net-worth-chart">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">
              {tx(lang, "Net worth over time", "Patrimonio neto en el tiempo", "Patrimonio neto en el tiempo")}
            </h3>
            <p className="text-xs text-slate-500">
              {tx(lang, "One sample per day (recorded when you open the dashboard).", "Una muestra al día (registro al abrir el dashboard).", "Una muestra al día (registro al abrir el dashboard).")}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {availableCurrencies.length > 0 && (
            <div className="flex gap-1 bg-slate-100 rounded-full p-1" data-testid="net-worth-currency">
              <button /* design-system: ALLOW legacy-todo */
                type="button"
                data-testid="net-worth-currency-all"
                onClick={() => setCurrency(null)}
                className={`px-3 py-1 text-xs font-bold rounded-full transition-colors ${
                  !currency ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
                }`}
              >
                {tx(lang, "All", "Todas", "Todas")}
              </button>
              {availableCurrencies.map((c) => (
                <button /* design-system: ALLOW legacy-todo */
                  key={c}
                  type="button"
                  data-testid={`net-worth-currency-${c}`}
                  onClick={() => setCurrency(c)}
                  className={`px-3 py-1 text-xs font-bold rounded-full transition-colors tabular-nums ${
                    currency === c ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
          <RangeSelector
            ranges={RANGES}
            value={days}
            onChange={setDays}
            testid="net-worth-range"
          />
        </div>
      </div>

      {mixedBases && (
        <div
          className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800"
          data-testid="net-worth-mixed-warning"
        >
          {tx(lang, `You're viewing snapshots in multiple currencies (${availableCurrencies.join(", ")}). Pick one for a consistent line.`, `Estás viendo instantáneas en varias monedas (${availableCurrencies.join(", ")}). Selecciona una para una línea consistente.`, `Estás viendo instantáneas en varias monedas (${availableCurrencies.join(", ")}). Selecciona una para una línea consistente.`)}
        </div>
      )}

      {isLoading ? (
        <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
          {tx(lang, "Loading…", "Cargando…", "Cargando…")}
        </div>
      ) : !hasEnoughPoints ? (
        <div className="h-48 flex flex-col items-center justify-center text-center text-slate-500 text-sm gap-1 bg-slate-50 rounded-xl border border-dashed border-slate-200" data-testid="net-worth-empty">
          <TrendingUp className="h-6 w-6 text-slate-400" />
          <span className="font-semibold text-slate-900">
            {tx(lang, "Not enough history yet", "Aún no hay suficiente historial", "Aún no hay suficiente historial")}
          </span>
          <span className="max-w-xs">
            {tx(lang, "Come back tomorrow — we record your net worth every day you open the dashboard.", "Vuelve mañana — registramos tu patrimonio neto cada día que entres al dashboard.", "Vuelve mañana — registramos tu patrimonio neto cada día que entres al dashboard.")}
          </span>
        </div>
      ) : (
        <div className="h-48" style={{ width: "100%", minWidth: 1 }} data-testid="net-worth-chart-canvas">
          <ResponsiveContainer width="100%" height={192}>
            <AreaChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="nwFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#16A34A" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#16A34A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis
                dataKey="day"
                tickFormatter={formatDayLabel}
                {...CHART_AXIS_PROPS}
              />
              <YAxis
                tickFormatter={formatCompactNumber}
                {...CHART_AXIS_PROPS}
                width={48}
              />
              <Tooltip
                formatter={(v) => formatMoney(v, displayCurrency)}
                labelFormatter={formatDateTooltip}
                contentStyle={CHART_TOOLTIP_STYLE}
              />
              <Area
                type="monotone"
                dataKey="net_worth"
                stroke="#16A34A"
                strokeWidth={2}
                fill="url(#nwFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
